import { FC, useMemo, } from 'react';
import { Col, Row } from 'antd';
import ScoreSummary from './components/ScoreSummary';
import ScoreSummaryHorizontal from './components/ScoreSummaryHorizontal';
import ScoreCard from './components/ScoreCard';
import FiveDimensional from '@/common/components/business/SiteMap/FiveDimensional';
import ImageLine from './components/ImageLine';
import { isArray, isNotEmpty } from '@lhb/func';
import { markTypeEnum } from '@/views/iterate/pages/siteselectionmapb/ts-config';
import styles from './index.module.less';
import cs from 'classnames';
import PredictSale from './components/PredictSale';
import ScoreExplaimIcon from '@/common/components/business/AreaScoreWithoutSum/ScoreExplaimIcon';
import FourNoSumCon from './components/FourNoSumCon';
import PredictingBanner from './components/PredictingBanner';

/**
 * @Description 评分
 * 注意该部分代码和点位测评详情对应部分是公共使用的，修改时一定要注意！！！
 */
const Evaluation: FC<{
  detail?: any;
  overView: any;
  picList: any[];
  configSwitch: any;
  shareParams?: any;
  // siteselectionmapb-选址地图；spotevaluation-点位测评
  provideFor?: string;
  isSemirModel?: boolean;
  /** 是否展示预测横幅 */
  showPredictingBanner?: boolean,
}> = ({
  detail,
  overView,
  picList,
  configSwitch,
  shareParams = {},
  // siteselectionmapb-选址地图；spotevaluation-点位测评
  provideFor = 'siteselectionmapb',
  isSemirModel = false,
  showPredictingBanner = false,
}) => {
  const showTotalScore = useMemo(() => overView?.showTotalScore, [overView]);
  // const cardView = useMemo(() => overView?.scores || [], [overView]);
  const cardView = useMemo(() => {
    const { scores } = overView || {};
    if (isArray(scores) && scores.length) {
      if (isSemirModel) { // 森马
        return scores.map((scoreItem: any, index: number) => {
          const target: any = {
            ...(scoreItem || {}),
          };
          const { score } = scoreItem || {};
          if (index === 0 || index === 3) {
            target.percent = score / 20 * 100;
          } else {
            target.percent = score / 30 * 100;
          }
          return target;
        });
      }
      return scores?.map((scoreItem: any) => ({
        ...scoreItem,
        percent: scoreItem?.score
      }));
    }
    return [];
  }, [overView, isSemirModel]);

  const isFiveDimen = useMemo(() => {
    return markTypeEnum.five === overView?.markType;
  }, [overView]);

  const isSixDimen = useMemo(() => {
    return markTypeEnum.six === overView?.markType;
  }, [overView]);

  const predictSale = useMemo(() => {
    const {
      salesAmountPredict, // 预测营业额
      lowSalesAmountPredict,
      upSalesAmountPredict,
    } = overView || {};

    // 优先展示区间，无区间值时展示具体值
    const canShowRange = isNotEmpty(lowSalesAmountPredict) && isNotEmpty(upSalesAmountPredict);
    const canShowValue = isNotEmpty(salesAmountPredict);

    return canShowRange || canShowValue;
  }, [overView]);

  /**
   * @description 四维展示
   */
  const renderFourDimen = () => {
    return (
      <div className={styles.fourDimenCon}>
        <ScoreSummaryHorizontal
          detail={detail}
          overviewData={overView}
          predictSale={predictSale}
          configSwitch={configSwitch}
          shareParams={shareParams}
          isSemirModel={isSemirModel}
        />
        <div className={styles.allScoreCon}>
          <Row gutter={[24, 8]}>
            {cardView.length ? cardView.map((item, index: number) => (
              <Col span={12} key={item?.name}>
                <ScoreCard
                  title={item?.name}
                  score={item?.score}
                  totalScore={item?.percent}
                  describe={item?.msg}
                  lineColor='#5B9FFF'
                  // 森马模型的维度中有四个字/五个字的区别， 为了展示美观
                  hasBlank={isSemirModel && (index === 1 || index === 3)}
                  isSemirModel={isSemirModel}
                />
              </Col>
            )) : <></>}
          </Row>
        </div>
      </div>
    );
  };

  /**
   * @description 四维无总分展示
   */
  const renderFourNoSumDimen = () => {
    return (
      <FourNoSumCon
        overViewData={overView}
        predictSale={predictSale}
        cardView={cardView}
        isSemirModel={isSemirModel}
      />
    );
  };

  /**
   * @description 五维/六维展示
   */
  const renderFiveSixDimen = () => {
    return (
      <div className={styles.fiveSixeDimenCon}>
        <div className={styles.halfWidth}>
          <ScoreSummary
            detail={detail}
            overviewData={overView}
            predictSale={predictSale}
            configSwitch={configSwitch}
            shareParams={shareParams}
            isSemirModel={isSemirModel}
          />
        </div>
        <div className={styles.halfWidth}>
          <div className={cs(styles.fiveDimensionChartWrapper, isFiveDimen && styles.fiveDimen, isSixDimen && styles.sixDimen)}>
            <FiveDimensional
              indicator={overView?.scores?.map((item) => ({
                ...item,
                value: item?.score || 0,
                max: 100
              }))}
              seriesDataValue={overView?.scores?.map((item) => item.score || 0)}
              height={isFiveDimen ? 174 : 180}
              radarRadius={isFiveDimen ? '51%' : '50%'}
              valueFontSize={12}
              backgroundColor='#ffffff'
            />
          </div>
        </div>

      </div>
    );
  };

  /**
   * @description 五维/六维无总分展示
   */
  const renderFiveSixNoSumDimen = () => {
    // `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_turnover@3x.png`
    return (
      <Row className={styles.fiveSixNoSumCon}>
        <Col span={predictSale ? 16 : 24}>
          <div className={styles.fiveSixNoSumChartWrapper}>
            <div className={styles.scoreTitle}>
              {isFiveDimen ? '五' : '六'}维评分
              {
                isSemirModel ? <></>
                  : <ScoreExplaimIcon
                    data={overView}
                    predictSale={predictSale}
                    className={styles.titleIcon}/>
              }
            </div>
            <FiveDimensional
              indicator={overView?.scores?.map((item) => ({
                ...item,
                value: item?.score || 0,
                max: 100
              }))}
              seriesDataValue={overView?.scores?.map((item) => item.score || 0)}
              height={180}
              radarRadius={isFiveDimen ? '51%' : '50%'}
              valueFontSize={12}
              backgroundColor='#F9FBFF'
              theme='blue'
            />
          </div>
        </Col>
        {predictSale ? <Col span={8}>
          <PredictSale overviewData={overView}/>
        </Col> : <></>}
      </Row>
    );
  };

  return (
    <>
      <div className={styles.evaluation}>
        {/* 四维一种布局，五维六维另一种布局,点位测评不需要区分是否展示总分 */}
        {showTotalScore || provideFor === 'spotevaluation'
          ? (isFiveDimen || isSixDimen ? renderFiveSixDimen() : renderFourDimen())
          : (isFiveDimen || isSixDimen ? renderFiveSixNoSumDimen() : renderFourNoSumDimen())}
        {/* 图片展示区域 */}
        {picList?.length ? <ImageLine values={picList} /> : ''}
      </div>

      {/* 预测横幅 */}
      {showPredictingBanner && <PredictingBanner type={detail?.type} score={overView?.marketScore} />}
    </>
  );
};

export default Evaluation;
